import { useEffect, useState } from 'react';
import { Alert, Button, Container } from 'reactstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IconArrowLeft,
  IconCalendar,
  IconCheck,
  IconClock,
  IconHeart,
  IconHome,
  IconLock,
  IconMapPin,
  IconUsers,
} from '@tabler/icons-react';
import { PropersAssignedPreSaleVisits } from './models/dashboardModels';
import { VisitPreSaleState } from '../../constants/visitsConstants';
import {
  getBadgeColorVisitPreSaleStatusAdapter,
  getVisitPreSaleStatusAdapter,
} from '../properties/adapters/visitsAdapter';
import { getStringDate } from '../../adapters/dateAdapter';
import {
  Field,
  Section,
  timeToMinutes,
  toInputDate,
} from './components/VisitDetailParts';
import usePreSaleVisitActions, {
  ProjectModelOption,
  REASSIGN_SDR_STATUS_NOT_ALLOWED,
  REASSIGN_SDR_VISIT_NOT_FOUND,
} from './hooks/usePreSaleVisitActions';
import { SdrListItem } from '../../hooks/useSdrList';
import useProperList from '../../hooks/useProperList';
import useAcceptGuestVisit from './hooks/useAcceptGuestVisit';
import usePreSaleVisitFeedback from './hooks/usePreSaleVisitFeedback';
import PreSaleFeedbackCard from './components/PreSaleFeedbackCard';
import useClientFeedback from './hooks/useClientFeedback';
import ClientFeedbackLinkCard from './components/ClientFeedbackLinkCard';
import ActivateEntityInvestorModal from '../../components/landlordInvestors/ActivateEntityInvestorModal';
import {
  VISIT_LOCATION_OFFICE,
  VISIT_TYPE_PRESENTIAL,
  visitLocationOptions,
  visitTypeOptions,
} from '../investors/constants/visitConstants';
import IntentSignalsCell from './components/IntentSignalsCell';
import PreSaleVisitFeedbackModal, {
  PreSaleVisitFeedback,
} from './components/PreSaleVisitFeedbackModal';
import InvestorFavoritesModal from './components/InvestorFavoritesModal';
import ReassignSdrModal from './components/ReassignSdrModal';
import VisitMarketplaceTypeBadge from '../../components/VisitMarketplaceTypeBadge';

interface LocationState {
  visit?: PropersAssignedPreSaleVisits;
}

const STEPS = [
  { key: VisitPreSaleState.PENDING_APPROVAL, label: 'Pendiente de aprobación' },
  { key: VisitPreSaleState.SCHEDULED, label: 'Programada' },
  { key: VisitPreSaleState.DONE, label: 'Finalizada' },
];

const getCurrentStep = (status: string): number => {
  switch (status) {
    case VisitPreSaleState.PENDING_APPROVAL:
      return 0;
    case VisitPreSaleState.SCHEDULED:
    case VisitPreSaleState.RESCHEDULED:
      return 1;
    case VisitPreSaleState.DONE:
      return 2;
    default:
      return -1; // cancelada / eliminada / anulada
  }
};

const PreSaleVisitDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // La lista no tiene un endpoint para pedir una visita por id, así que el
  // detalle se abre con la visita que ya cargó el dashboard (router state).
  const visit = (location.state as LocationState | null)?.visit;

  const {
    availableTimes,
    doLoadAvailableTimes,
    doRescheduleVisit,
    cancellationReasons,
    doMarkAsDone,
    doLoadProjectModels,
    doSendFeedback,
    doCancelVisit,
    doReassignSdr,
    doLoadCancellationReasons,
  } = usePreSaleVisitActions();
  const { propersOptions } = useProperList('for_pre_sale');
  const [selectedProperId, setSelectedProperId] = useState('');
  const [acceptOpen, setAcceptOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackSaving, setFeedbackSaving] = useState(false);
  const [feedbackError, setFeedbackError] = useState('');
  const [projectModels, setProjectModels] = useState<ProjectModelOption[]>([]);
  const [activateOpen, setActivateOpen] = useState(false);
  // Feedback del Proper (sólo lectura para el admin).
  const savedFeedback = usePreSaleVisitFeedback(
    visit?.id,
    visit?.visitInfo.status === VisitPreSaleState.DONE,
    true,
  );
  const clientFeedback = useClientFeedback(
    visit?.id,
    visit?.visitInfo.status === VisitPreSaleState.DONE,
  );
  const accept = useAcceptGuestVisit(
    visit?.id ?? '',
    visit?.visitInfo.status === VisitPreSaleState.PENDING_APPROVAL,
  );
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [newDate, setNewDate] = useState(() =>
    toInputDate(visit?.visitInfo.visit_date),
  );
  // Fecha/hora con las que ya está agendada: se precargan en "Reagendar".
  const originalDate = toInputDate(visit?.visitInfo.visit_date);
  const originalMinutes = timeToMinutes(visit?.visitInfo.visit_time);
  const [newTime, setNewTime] = useState(
    originalMinutes !== undefined ? String(originalMinutes) : '',
  );
  const [rescheduleError, setRescheduleError] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelExplain, setCancelExplain] = useState('');
  const [cancelVoided, setCancelVoided] = useState(false);
  const [favoritesOpen, setFavoritesOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignVisitMissing, setReassignVisitMissing] = useState(false);
  const [reassignSuccess, setReassignSuccess] = useState('');

  useEffect(() => {
    if (!reassignSuccess) return;
    const timer = setTimeout(() => setReassignSuccess(''), 5000);
    return () => clearTimeout(timer);
  }, [reassignSuccess]);

  useEffect(() => {
    if (visit) doLoadAvailableTimes(visit.projectInfo.id, originalDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visit?.id]);

  useEffect(() => {
    if (visit) doLoadCancellationReasons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visit?.id]);

  useEffect(() => {
    if (visit?.visitInfo.status === VisitPreSaleState.DONE) {
      doLoadProjectModels(visit.projectInfo.id).then(setProjectModels);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visit?.id]);

  const goBack = () => navigate(-1);

  if (!visit) {
    return (
      <Container className="mt-6 visit-detail-v2" fluid>
        <Button className="vd-back" onClick={goBack}>
          <IconArrowLeft size={16} /> Volver a visitas
        </Button>
        <section className="vd-card">
          <p className="mb-0 text-muted">
            No se encontró la asesoría. Abrila desde la lista de PreVenta del
            dashboard.
          </p>
        </section>
      </Container>
    );
  }

  const { visitInfo, projectInfo, properInfo, sdrInfo } = visit;
  const status = visitInfo.status;
  const currentStep = getCurrentStep(status);
  const isDone = status === VisitPreSaleState.DONE;
  const feedbackModelName = projectModels.find(
    (m) => m.id === savedFeedback.feedback?.project_model_id,
  )?.name;
  const canMarkDone = [
    VisitPreSaleState.SCHEDULED,
    VisitPreSaleState.RESCHEDULED,
  ].includes(status);
  // Reagendar y cancelar también aplican mientras está pendiente de aprobación.
  const canAct = canMarkDone || status === VisitPreSaleState.PENDING_APPROVAL;

  // Pendiente de aprobación: se acepta con el flujo que ya existe (detalle de
  // asesoría de invitado: tipo de visita, ubicación, proper y confirmación).
  const canAccept = status === VisitPreSaleState.PENDING_APPROVAL;

  const runAccept = async () => {
    setActivateOpen(false);
    if (await accept.accept()) goBack();
  };

  const handleAcceptClick = () => {
    if (accept.isGuest) setActivateOpen(true);
    else runAccept();
  };

  const toggleAccept = () => {
    setRescheduleOpen(false);
    setCancelOpen(false);
    setAcceptOpen((open) => !open);
  };

  const handleDone = async () => {
    setFeedbackError('');
    setFeedbackOpen(true);
    setProjectModels(await doLoadProjectModels(projectInfo.id));
  };

  const submitFeedback = async (feedback: PreSaleVisitFeedback) => {
    setFeedbackSaving(true);
    setFeedbackError('');
    try {
      const result = await doSendFeedback(visit.id, feedback);
      if (!result.ok) {
        setFeedbackError(result.message || 'No se pudo guardar el feedback.');
        return;
      }
      if ((await doMarkAsDone(visit.id)) === 200) goBack();
      else setFeedbackError('El feedback se guardó, pero no se pudo marcar la visita como realizada. Intenta de nuevo.');
    } finally {
      setFeedbackSaving(false);
    }
  };

  const openReschedule = () => {
    setCancelOpen(false);
    setAcceptOpen(false);
    setRescheduleError('');
    setRescheduleOpen((open) => !open);
  };

  // Horarios del día elegido; el de esta misma visita siempre es elegible.
  const isOriginalDay = newDate === originalDate;
  const rescheduleSlots = (() => {
    const slots = availableTimes.map((slot) => ({
      ...slot,
      is_blocked:
        !!slot.is_blocked &&
        !(isOriginalDay && slot.minutes === originalMinutes),
    }));
    if (
      isOriginalDay &&
      originalMinutes !== undefined &&
      !slots.some((slot) => slot.minutes === originalMinutes)
    ) {
      slots.unshift({
        minutes: originalMinutes,
        time: visitInfo.visit_time,
        is_blocked: false,
      });
    }
    return slots;
  })();
  const rescheduleChanged =
    newDate !== originalDate || Number(newTime) !== originalMinutes;

  const handleDateChange = (value: string) => {
    setNewDate(value);
    setNewTime('');
    doLoadAvailableTimes(projectInfo.id, value);
  };

  const handleReschedule = async () => {
    const properId = properInfo?.id || selectedProperId;
    if (!newDate || newTime === '') return;
    if (!properId) {
      setRescheduleError(
        'Selecciona un proper para reagendar esta visita.',
      );
      return;
    }
    const result = await doRescheduleVisit(
      visit.id,
      newDate,
      Number(newTime),
      properId,
    );
    if (result === 200) goBack();
    else setRescheduleError(`(${result}) No fue posible reagendar la visita`);
  };

  const discardReschedule = () => {
    setRescheduleOpen(false);
    setRescheduleError('');
    setNewDate(originalDate);
    setNewTime(originalMinutes !== undefined ? String(originalMinutes) : '');
    doLoadAvailableTimes(projectInfo.id, originalDate);
  };

  const discardCancel = () => {
    setCancelOpen(false);
    setCancelReason('');
    setCancelExplain('');
    setCancelVoided(false);
  };

  const handleCancel = async () => {
    if (!cancelReason) return;
    const result = await doCancelVisit(
      visit.id,
      cancelReason,
      cancelExplain,
      cancelVoided ? VisitPreSaleState.VOIDED : '',
    );
    if (result === 200) goBack();
  };

  // La visita sólo vive en el router state: `navigate(0)` la dejaría igual, así
  // que los cambios se escriben ahí para que sobrevivan a una recarga.
  const replaceVisit = (updated: PropersAssignedPreSaleVisits) => {
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: { visit: updated } },
    );
  };

  const handleReassign = async (sdrId: string) => {
    const result = await doReassignSdr(visit.id, sdrId);
    if (!result.ok && result.code === REASSIGN_SDR_VISIT_NOT_FOUND) {
      setReassignVisitMissing(true);
    }
    if (
      !result.ok &&
      result.code === REASSIGN_SDR_STATUS_NOT_ALLOWED &&
      result.visitStatus
    ) {
      replaceVisit({
        ...visit,
        visitInfo: { ...visit.visitInfo, status: result.visitStatus },
      });
    }
    return result;
  };

  const handleReassigned = ({ id, name, avatar }: SdrListItem) => {
    replaceVisit({ ...visit, sdrInfo: { id, name, avatar } });
    setReassignOpen(false);
    setReassignSuccess(`SDR reasignado a ${name}.`);
  };

  const closeReassign = () => {
    if (reassignVisitMissing) goBack();
    else setReassignOpen(false);
  };

  return (
    <Container className="mt-6 visit-detail-v2" fluid>
      {reassignSuccess && (
        // Mismo toast fijo que InternalUserDashboard: el Toast de reactstrap se
        // renderiza sin estilos en este build.
        <div
          style={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 2000,
            minWidth: 320,
            maxWidth: 420,
          }}
        >
          <Alert
            color="success"
            toggle={() => setReassignSuccess('')}
            className="shadow"
          >
            <strong>Éxito: </strong>
            {reassignSuccess}
          </Alert>
        </div>
      )}

      <Button className="vd-back" onClick={goBack}>
        <IconArrowLeft size={16} /> Volver a visitas
      </Button>

      <section className="vd-card vd-hero">
        <div className="vd-hero__top">
          <span className="vd-icon vd-icon--green vd-icon--lg">
            <IconHome size={26} />
          </span>
          <div>
            <div className="vd-hero__title-row">
              <h1 className="vd-hero__title">{projectInfo.name}</h1>
              <span
                className={`vd-pill vd-pill--${getBadgeColorVisitPreSaleStatusAdapter(
                  status,
                )}`}
              >
                {getVisitPreSaleStatusAdapter(status)}
              </span>
            </div>
            <div className="vd-hero__sub">
              <IconMapPin size={14} /> {projectInfo.address}
            </div>
            <div className="vd-hero__code">
              [{projectInfo.code}] · {visit.id}
            </div>
          </div>
        </div>

        {canAct && (
          <div className="vd-actions">
            {canAccept && (
              <button
                type="button"
                className="vd-btn vd-btn--primary"
                onClick={toggleAccept}
                aria-expanded={acceptOpen}
              >
                <IconCheck size={16} /> Aceptar asesoría
              </button>
            )}
            {canMarkDone && (
              <button
                type="button"
                className="vd-btn vd-btn--primary"
                onClick={handleDone}
              >
                <IconCheck size={16} /> Marcar como realizada
              </button>
            )}
            <button
              type="button"
              className="vd-btn vd-btn--warning"
              onClick={openReschedule}
              aria-expanded={rescheduleOpen}
            >
              Reagendar
            </button>
            <button
              type="button"
              className="vd-btn vd-btn--warning"
              onClick={() => setReassignOpen(true)}
            >
              <IconUsers size={16} /> Reasignar SDR
            </button>
            <button
              type="button"
              className="vd-btn vd-btn--danger"
              onClick={() => {
                setRescheduleOpen(false);
                setAcceptOpen(false);
                setCancelOpen((open) => !open);
              }}
              aria-expanded={cancelOpen}
            >
              Cancelar
            </button>
          </div>
        )}

        {canAccept && acceptOpen && (
          <div className="vd-panel vd-panel--success vd-accept">
            <div className="vd-accept__head">
              <span className="vd-icon vd-icon--green">
                <IconCheck size={18} />
              </span>
              <div>
                <h3 className="vd-panel__title">Aceptar asesoría</h3>
                <p className="vd-accept__sub">
                  Define cómo será la asesoría y quién la atenderá.
                </p>
              </div>
            </div>

            {!accept.guestVisit ? (
              <p className="vd-panel__hint">
                {accept.error || 'Cargando detalles de la asesoría...'}
              </p>
            ) : (
              <>
                <div className="vd-accept__grid">
                  <div className="vd-accept__field">
                    <span className="vd-field__label">Tipo de asesoría</span>
                    <div className="vd-segment" role="group">
                      {visitTypeOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={
                            accept.visitType === option.value
                              ? 'is-active'
                              : ''
                          }
                          aria-pressed={accept.visitType === option.value}
                          onClick={() => accept.setVisitType(option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {accept.visitType === VISIT_TYPE_PRESENTIAL && (
                    <div className="vd-accept__field">
                      <span className="vd-field__label">Lugar</span>
                      <div className="vd-segment" role="group">
                        {visitLocationOptions.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={
                              accept.locationKey === option.value
                                ? 'is-active'
                                : ''
                            }
                            aria-pressed={accept.locationKey === option.value}
                            onClick={() =>
                              accept.setSelectedLocation(option.value)
                            }
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <label className="vd-accept__field">
                    <span className="vd-field__label">Proper asignado</span>
                    <select
                      className="vd-input"
                      value={accept.properId}
                      onChange={(e) => accept.setProperId(e.target.value)}
                    >
                      <option value="">Selecciona un proper</option>
                      {propersOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="vd-accept__grid">
                  <label className="vd-accept__field">
                    <span className="vd-field__label">
                      Fecha de la asesoría
                    </span>
                    <input
                      type="date"
                      className="vd-input"
                      value={accept.newDate}
                      min={getStringDate(new Date())}
                      onChange={(e) => accept.setNewDate(e.target.value)}
                    />
                    <span className="vd-accept__hint">
                      Agendada para {visitInfo.visit_date} · {visitInfo.visit_time}.
                      Cámbiala solo si hace falta.
                    </span>
                  </label>
                  <label className="vd-accept__field">
                    <span className="vd-field__label">Hora de la asesoría</span>
                    <select
                      className="vd-input"
                      value={accept.newTime}
                      onChange={(e) => accept.setNewTime(e.target.value)}
                      disabled={!accept.newDate}
                    >
                      <option value="">
                        {!accept.newDate
                          ? 'Elige primero una fecha'
                          : accept.timeSlots.length
                            ? 'Selecciona una hora'
                            : 'Sin horarios disponibles'}
                      </option>
                      {accept.timeSlots.map((slot) => (
                        <option
                          key={slot.minutes}
                          value={slot.minutes}
                          disabled={slot.isBlocked}
                        >
                          {slot.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {accept.visitType === VISIT_TYPE_PRESENTIAL && (
                  <div className="vd-accept__address">
                    <IconMapPin size={16} />
                    <span>
                      {accept.locationKey === VISIT_LOCATION_OFFICE
                        ? (accept.officeAddress?.address ?? 'Oficina Propi')
                        : accept.guestVisit.projectInfo.address}
                    </span>
                  </div>
                )}

                {accept.error && (
                  <p className="vd-panel__error">{accept.error}</p>
                )}

                <div className="vd-accept__footer">
                  <button
                    type="button"
                    className="vd-btn vd-btn--ghost"
                    onClick={() => setAcceptOpen(false)}
                  >
                    Descartar
                  </button>
                  <button
                    type="button"
                    className="vd-btn vd-btn--primary"
                    onClick={handleAcceptClick}
                    disabled={!accept.properId || accept.isSubmitting}
                  >
                    <IconCheck size={16} /> Confirmar y aceptar
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {canAct && rescheduleOpen && (
          <div className="vd-panel vd-panel--warning">
            <h3 className="vd-panel__title">Reagendar visita</h3>
            <div className="vd-panel__row">
              <label className="vd-panel__field">
                Nueva fecha
                <input
                  type="date"
                  className="vd-input"
                  value={newDate}
                  min={getStringDate(new Date())}
                  onChange={(e) => handleDateChange(e.target.value)}
                />
              </label>
              <label className="vd-panel__field">
                Nueva hora
                <select
                  className="vd-input"
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  disabled={!newDate}
                >
                  <option value="">
                    {rescheduleSlots.length || !newDate
                      ? 'Selecciona una hora'
                      : 'Sin horarios disponibles'}
                  </option>
                  {rescheduleSlots.map((slot) => (
                    <option
                      key={slot.minutes}
                      value={slot.minutes}
                      disabled={!!slot.is_blocked}
                    >
                      {slot.time}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            {!properInfo?.id && (
              <label className="vd-panel__field">
                Proper
                <select
                  className="vd-input"
                  value={selectedProperId}
                  onChange={(e) => setSelectedProperId(e.target.value)}
                >
                  <option value="">Selecciona un proper</option>
                  {propersOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {rescheduleError && (
              <p className="vd-panel__error">{rescheduleError}</p>
            )}
            <div className="vd-panel__actions">
              <button
                type="button"
                className="vd-btn vd-btn--solid-warning"
                onClick={handleReschedule}
                disabled={!newDate || newTime === '' || !rescheduleChanged}
              >
                Confirmar nueva cita
              </button>
              <button
                type="button"
                className="vd-btn vd-btn--ghost"
                onClick={discardReschedule}
              >
                Descartar
              </button>
            </div>
          </div>
        )}

        {canAct && cancelOpen && (
          <div className="vd-cancel">
            <h3 className="vd-cancel__title">Cancelar visita</h3>
            <select
              className="vd-input"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              aria-label="Motivo de la cancelación"
            >
              <option value="">Selecciona un motivo</option>
              {cancellationReasons.map((reason) => (
                <option key={reason} value={reason}>
                  {reason}
                </option>
              ))}
            </select>
            <textarea
              className="vd-input"
              rows={2}
              value={cancelExplain}
              onChange={(e) => setCancelExplain(e.target.value)}
              placeholder="Comentario adicional (opcional)"
            />
            <label className="vd-check">
              <input
                type="checkbox"
                checked={cancelVoided}
                onChange={(e) => setCancelVoided(e.target.checked)}
              />
              Registrar como anulada
            </label>
            <div className="vd-cancel__actions">
              <button
                type="button"
                className="vd-btn vd-btn--solid-danger"
                onClick={handleCancel}
                disabled={!cancelReason}
              >
                Confirmar cancelación
              </button>
              <button
                type="button"
                className="vd-btn vd-btn--ghost"
                onClick={discardCancel}
              >
                Descartar
              </button>
            </div>
          </div>
        )}
      </section>

      <div className="vd-layout">
        <div className="vd-col">
        <Section
          icon={<IconUsers size={20} />}
          tone="blue"
          title="Cliente"
        >
          <div className="vd-grid">
            <Field label="Nombre">{visitInfo.investor_name}</Field>
            <Field label="Correo">{visitInfo.investor_email}</Field>
            <Field label="Teléfono">{visitInfo.investor_phone}</Field>
            <Field label="Origen">
              <VisitMarketplaceTypeBadge
                marketplace_type={visitInfo.marketplace_type}
              />
            </Field>
          </div>
        </Section>

        <Section
          icon={<IconCalendar size={20} />}
          tone="green"
          title="Visita"
        >
          <div className="vd-grid">
            <Field label="Fecha">{visitInfo.visit_date}</Field>
            <Field label="Hora">{visitInfo.visit_time}</Field>
            <Field label="Proper">{properInfo?.name}</Field>
            <Field label="SDR">{sdrInfo?.name}</Field>
          </div>
          <div className="vd-signals">
            <span className="vd-field__label">Señales de intención</span>
            <IntentSignalsCell
              favorites={visitInfo.investor_favorites}
              visitDate={visitInfo.visit_date}
              visitTime={visitInfo.visit_time}
              status={status}
              isPreSale
              onViewFavorites={() => setFavoritesOpen(true)}
            />
            {(visitInfo.investor_favorites?.length ?? 0) > 0 && (
              <button
                type="button"
                className="vd-link"
                onClick={() => setFavoritesOpen(true)}
              >
                <IconHeart size={14} /> Ver favoritos
              </button>
            )}
          </div>
        </Section>

        </div>
        <div className="vd-col">
        <Section
          icon={<IconClock size={20} />}
          tone="orange"
          title="Estado de la visita"
        >
          {currentStep === -1 ? (
            <p className="mb-0">
              Esta asesoría está{' '}
              <strong>
                {getVisitPreSaleStatusAdapter(status).toLowerCase()}
              </strong>
              .
            </p>
          ) : (
            <ol
              className={`vd-steps vd-steps--${getBadgeColorVisitPreSaleStatusAdapter(
                status,
              )}`}
            >
              {STEPS.map((step, index) => (
                <li
                  key={step.key}
                  className={`vd-step${
                    index < currentStep ? ' is-done' : ''
                  }${index === currentStep ? ' is-current' : ''}`}
                >
                  <span className="vd-step__dot">
                    {index < currentStep ? <IconCheck size={12} /> : index + 1}
                  </span>
                  <span className="vd-step__text">
                    <span className="vd-step__label">{step.label}</span>
                    {index === currentStep && (
                      <span className="vd-step__current">Estado actual</span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </Section>
        {isDone && (
          <ClientFeedbackLinkCard
            state={clientFeedback.state}
            answer={clientFeedback.answer}
            link={clientFeedback.link}
          />
        )}
        {isDone && (
          <PreSaleFeedbackCard
            state={savedFeedback.state}
            feedback={savedFeedback.feedback}
            modelName={feedbackModelName}
          />
        )}
        <section className="vd-card vd-locked">
          <header className="vd-card__header">
            <span className="vd-icon vd-icon--muted">
              <IconLock size={20} />
            </span>
            <h2 className="vd-card__title">Reporte del propietario</h2>
          </header>
          <p className="vd-locked__text">
            Se habilita cuando la visita esté finalizada y se reciba el feedback
            del Proper y la respuesta del cliente.
          </p>
        </section>
        </div>
      </div>

      <PreSaleVisitFeedbackModal
        isOpen={feedbackOpen}
        projectName={projectInfo.name}
        models={projectModels.map((m) => ({ value: m.id, label: m.name }))}
        error={feedbackError}
        loading={feedbackSaving}
        onClose={() => setFeedbackOpen(false)}
        onSubmit={submitFeedback}
      />
      <InvestorFavoritesModal
        isOpen={favoritesOpen}
        toggle={() => setFavoritesOpen(false)}
        investorName={visitInfo.investor_name}
        favorites={visitInfo.investor_favorites || []}
        currentListingId={projectInfo.id}
      />
      {reassignOpen && (
        <ReassignSdrModal
          currentSdr={sdrInfo}
          onClose={closeReassign}
          onReassign={handleReassign}
          onReassigned={handleReassigned}
        />
      )}
      {accept.guestVisit && (
        <ActivateEntityInvestorModal
          guestId={accept.guestVisit.userInfo.id}
          isOpen={activateOpen}
          onToggle={() => setActivateOpen(false)}
          onConfirmation={runAccept}
          bodyText="Al activar el guest aceptas todas las asesorías del mismo. Además vuelves al guest un Inversonista."
          confirmationText="Entiendo, continuar"
          abortText="No, seguir revisando..."
          investor={accept.guestVisit}
        />
      )}
    </Container>
  );
};

export default PreSaleVisitDetail;
